import { useCallback, useEffect, useMemo, useState } from "react";
import { getArchiveReport, getSignedVideoUrl, resendReportNotification, sendReportEmail, sendReportAlimtalk } from "../lib/admin.js";
import AdminSidebar from "./AdminSidebar.jsx";
import SimilarCases from "./SimilarCases.jsx";
import { ReportView } from "../parent/screens.jsx";
import { SYMPTOM_LABEL_MAP } from "../lib/symptoms.js";

// 발행 완료 케이스의 회고 페이지. 좌측은 운영진 전용 컨텍스트(원본 영상·증상
// 체크리스트·짱샘 메모·메타), 우측은 phone-frame 안에 부모가 본 ReportView를
// 그대로 띄운다. AdminArchive의 행을 클릭하면 이 페이지로 이동한다.

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatAge(birthMonth) {
  if (!birthMonth) return "";
  const b = new Date(birthMonth);
  const n = new Date();
  const months =
    (n.getFullYear() - b.getFullYear()) * 12 + (n.getMonth() - b.getMonth());
  if (months < 12) return `${Math.max(0, months)}개월`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem === 0 ? `${years}세` : `${years}세 ${rem}개월`;
}

function formatDuration(seconds) {
  if (seconds == null) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// 두 시각 차이를 "Xh Ym" 또는 "Xd Yh" 형태로
function formatElapsed(fromIso, toIso) {
  if (!fromIso || !toIso) return "-";
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "-";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}분`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}시간 ${mins % 60}분`;
  const days = Math.floor(hrs / 24);
  return `${days}일 ${hrs % 24}시간`;
}

export default function AdminArchiveDetail({ caseId, onBack, onSignOut }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 비디오 + 일러스트 + 비디오 썸네일에 사용할 signed URL 풀.
  // path → 1시간짜리 signed URL.
  const [signedUrls, setSignedUrls] = useState({});
  const [activeVideoIdx, setActiveVideoIdx] = useState(0);

  // 재발송 도구 상태. resend = it_notifications에 재안내 row insert,
  // copyHint = 카톡에 붙여넣을 안내 문구를 클립보드에 복사.
  const [resendStatus, setResendStatus] = useState("idle"); // 'idle' | 'sending' | 'ok' | 'err'
  const [resendError, setResendError] = useState(null);
  const [copyStatus, setCopyStatus] = useState("idle"); // 'idle' | 'ok' | 'err'
  const [emailStatus, setEmailStatus] = useState("idle"); // 'idle' | 'sending' | 'ok' | 'err'
  const [emailError, setEmailError] = useState(null);
  const [atalkStatus, setAtalkStatus] = useState("idle");
  const [atalkError, setAtalkError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await getArchiveReport(caseId);
      if (!d) {
        setError("케이스를 찾을 수 없어요.");
        return;
      }
      setData(d);
      setActiveVideoIdx(0);
    } catch (err) {
      setError(err.message ?? "불러오기 실패");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  // 영상 6개 썸네일 + 현재 active 영상의 본체 signed URL을 일괄로 발급.
  // path를 키로 캐시해서 active를 바꿔도 같은 영상은 다시 요청하지 않는다.
  useEffect(() => {
    if (!data) return;
    const targets = new Set();
    (data.videos ?? []).forEach((v) => {
      if (v.thumbnail_path) targets.add(v.thumbnail_path);
    });
    const active = data.videos?.[activeVideoIdx];
    if (active?.storage_path) targets.add(active.storage_path);
    const illustrations = data.report?.content?.illustrations ?? [];
    illustrations.forEach((r) => {
      if (r?.storage_path) targets.add(r.storage_path);
    });

    const missing = [...targets].filter((p) => !signedUrls[p]);
    if (!missing.length) return;
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(missing.map(async (path) => {
        try {
          next[path] = await getSignedVideoUrl(path);
        } catch { /* skip — 슬롯은 placeholder로 떨어짐 */ }
      }));
      if (cancelled || !Object.keys(next).length) return;
      setSignedUrls((prev) => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [data, activeVideoIdx, signedUrls]);

  const resend = async () => {
    if (resendStatus === "sending") return;
    if (!window.confirm("보호자에게 '리포트를 다시 안내드려요' 알림을 보낼까요?\n앱을 켤 때 알림 목록에 표시됩니다.")) return;
    setResendStatus("sending");
    setResendError(null);
    try {
      await resendReportNotification({ caseId });
      setResendStatus("ok");
      // 3초 뒤 다시 누를 수 있도록 idle로 되돌림
      setTimeout(() => setResendStatus("idle"), 3000);
    } catch (err) {
      setResendStatus("err");
      setResendError(err.message ?? "재알림 실패");
    }
  };

  const sendEmail = async () => {
    if (emailStatus === "sending") return;
    if (!window.confirm("보호자에게 리포트 도착 이메일을 보낼까요?")) return;
    setEmailStatus("sending");
    setEmailError(null);
    try {
      const result = await sendReportEmail({ caseId });
      setEmailStatus("ok");
      setTimeout(() => setEmailStatus("idle"), 3000);
    } catch (err) {
      setEmailStatus("err");
      setEmailError(err.message ?? "이메일 발송 실패");
    }
  };

  const sendAlimtalk = async () => {
    if (atalkStatus === "sending") return;
    if (!window.confirm("보호자에게 카카오 알림톡을 보낼까요?")) return;
    setAtalkStatus("sending");
    setAtalkError(null);
    try {
      await sendReportAlimtalk({ caseId });
      setAtalkStatus("ok");
      setTimeout(() => setAtalkStatus("idle"), 3000);
    } catch (err) {
      setAtalkStatus("err");
      setAtalkError(err.message ?? "알림톡 발송 실패");
    }
  };

  const copyHint = async () => {
    if (!data) return;
    const name = data.meta?.child?.name ?? "아이";
    const tag = data.caseRecord?.concern_tags?.[0];
    const text =
      `${name} 어머님께,\n\n` +
      `이전에 보내드린 ${tag ? `${tag} 관찰 ` : ""}리포트가 i-talk 앱에 그대로 남아 있어요. ` +
      `아래 링크로 로그인하시면 다시 열어보실 수 있어요.\n\n` +
      `https://talk.junominu.com\n\n` +
      `궁금한 점은 짱샘 카카오톡 오픈채팅(https://open.kakao.com/o/s3YnSoni)으로 편하게 문의해 주세요.\n\n` +
      `— 짱샘 드림`;
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus("ok");
      setTimeout(() => setCopyStatus("idle"), 2500);
    } catch {
      // navigator.clipboard 권한이 없는 환경(예: HTTP) — fallback execCommand
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.cssText = "position:fixed;left:-9999px;top:0;";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
        setCopyStatus("ok");
        setTimeout(() => setCopyStatus("idle"), 2500);
      } catch {
        setCopyStatus("err");
      }
    }
  };

  const symptomEntries = useMemo(() => {
    const map = data?.observations?.symptoms ?? {};
    return Object.entries(map)
      .filter(([, v]) => v)
      .map(([k]) => SYMPTOM_LABEL_MAP[k] ?? k);
  }, [data]);

  if (loading || !data) {
    return (
      <div className="admin">
        <AdminSidebar active="archive" onSignOut={onSignOut}/>
        <main className="admin-main">
          <button onClick={onBack} className="btn btn-outline" style={{padding:'6px 12px',fontSize:12,marginBottom:14}}>
            ← 아카이브 목록
          </button>
          {error ? (
            <div style={{background:'#fef9f0',border:'1px solid #e74c3c44',borderRadius:12,padding:'14px 16px',color:'#c0392b',fontSize:13}}>
              {error}
            </div>
          ) : (
            <div style={{textAlign:'center',padding:'80px 0'}}>
              <div className="spinner" style={{display:'inline-block'}}/>
            </div>
          )}
        </main>
      </div>
    );
  }

  const { caseRecord, report, meta, videos, research } = data;
  const videoColorsA = ["#2c5f8d", "#e17055", "#78c1c8", "#e8a87c", "#9b89b3", "#5b7b9a"];
  const videoColorsB = ["#3a7ab5", "#e8a87c", "#00b894", "#ff9933", "#6c5ce7", "#2c5f8d"];
  const activeVideo = videos?.[activeVideoIdx];
  const activeUrl = activeVideo?.storage_path ? signedUrls[activeVideo.storage_path] : null;

  return (
    <div className="admin">
      <AdminSidebar active="archive" onSignOut={onSignOut}/>

      <main className="admin-main" style={{paddingRight:28}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6,flexWrap:'wrap'}}>
          <button onClick={onBack} className="btn btn-outline" style={{padding:'6px 12px',fontSize:12}}>
            ← 아카이브 목록
          </button>
          <h1 className="admin-h1" style={{margin:0}}>
            {meta.child?.name ?? "(이름 없음)"}
            {formatAge(meta.child?.birth_month) && (
              <span style={{color:'#718096',fontWeight:500,fontSize:18,marginLeft:8}}>
                · {formatAge(meta.child?.birth_month)}
              </span>
            )}
          </h1>
          <span style={{padding:'4px 10px',borderRadius:9999,fontSize:11,fontWeight:700,background:'#27ae6022',color:'#1f7a3f'}}>
            전송 완료
          </span>
          {meta.ai_model && (
            <span style={{padding:'4px 10px',borderRadius:9999,fontSize:11,fontWeight:700,background:'#2c5f8d14',color:'#2c5f8d'}}>
              {meta.ai_model}
            </span>
          )}
        </div>
        <p className="admin-sub" style={{marginBottom:18}}>
          {meta.parent?.email ?? "보호자 미상"}
          {meta.parent?.display_name ? ` · ${meta.parent.display_name}` : ""}
          {" · 발행 "}{formatDateTime(meta.sent_at)}
          {report?.viewed_at
            ? <> · <span style={{color:'#1f7a3f',fontWeight:700}}>보호자 확인 {formatDateTime(report.viewed_at)}</span></>
            : <> · <span style={{color:'#c0392b',fontWeight:700}}>보호자 미확인</span></>}
        </p>

        <div className="archive-retro">
          <div className="archive-retro-info">

            {/* 영상 플레이어 + 썸네일 strip */}
            <Section title={`🎬 원본 영상 ${videos.length}개`}>
              {videos.length === 0 ? (
                <div style={{fontSize:12,color:'#718096'}}>업로드된 영상이 없어요.</div>
              ) : (
                <>
                  <div style={{
                    background:'linear-gradient(135deg,#1a1a2e,#2c5f8d)',
                    borderRadius:12,position:'relative',overflow:'hidden',
                    aspectRatio:'9 / 16',maxWidth:280,marginInline:'auto',marginBottom:10,
                  }}>
                    {activeUrl ? (
                      <video
                        key={activeUrl}
                        src={activeUrl}
                        controls
                        playsInline
                        preload="metadata"
                        style={{width:'100%',height:'100%',objectFit:'contain',background:'#000'}}
                      />
                    ) : (
                      <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff'}}>
                        <div className="spinner"/>
                      </div>
                    )}
                    {activeVideo && (
                      <div style={{position:'absolute',top:8,left:8,background:'rgba(0,0,0,.55)',color:'#fff',padding:'3px 8px',borderRadius:4,fontSize:11,fontWeight:700}}>
                        #{activeVideo.position} · {formatDuration(activeVideo.duration_seconds)}
                      </div>
                    )}
                  </div>
                  <div className="video-strip">
                    {videos.map((v, idx) => {
                      const colorA = videoColorsA[(v.position - 1) % 6];
                      const colorB = videoColorsB[(v.position - 1) % 6];
                      const thumb = v.thumbnail_path ? signedUrls[v.thumbnail_path] : null;
                      const baseStyle = thumb
                        ? { backgroundImage:`url(${thumb})`, backgroundSize:'cover', backgroundPosition:'center', backgroundColor:'#000' }
                        : { background:`linear-gradient(135deg, ${colorA}, ${colorB})` };
                      return (
                        <div
                          key={v.id}
                          className={"thumb" + (activeVideoIdx === idx ? " active" : "")}
                          onClick={() => setActiveVideoIdx(idx)}
                          style={{...baseStyle, textShadow:'0 1px 3px rgba(0,0,0,.6)'}}
                        >
                          #{v.position}
                          <div style={{position:'absolute',bottom:4,right:4,background:'rgba(0,0,0,.55)',padding:'1px 5px',borderRadius:3,fontSize:9,textShadow:'none'}}>
                            {formatDuration(v.duration_seconds)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </Section>

            {/* 보호자가 입력한 메모 */}
            {meta.concern_memo && (
              <Section title="👪 보호자 메모">
                <div style={{fontSize:13,color:'#1a1a2e',lineHeight:1.7,whiteSpace:'pre-wrap'}}>
                  {meta.concern_memo}
                </div>
              </Section>
            )}

            {/* 짱샘이 워크스페이스에서 직접 적은 메모 */}
            {data.observations?.notes && (
              <Section title="📝 짱샘 관찰 메모">
                <div style={{fontSize:13,color:'#1a1a2e',lineHeight:1.7,whiteSpace:'pre-wrap'}}>
                  {data.observations.notes}
                </div>
                {data.observations.updated_at && (
                  <div style={{fontSize:10,color:'#a0aec0',marginTop:8}}>
                    최종 수정 {formatDateTime(data.observations.updated_at)}
                  </div>
                )}
              </Section>
            )}

            {/* 체크된 증상 — 카테고리별로 묶지 않고 평탄하게 칩으로 */}
            {symptomEntries.length > 0 && (
              <Section title={`✅ 체크된 증상 ${symptomEntries.length}개`}>
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {symptomEntries.map((label) => (
                    <span key={label} style={{
                      fontSize:11,fontWeight:600,padding:'4px 10px',borderRadius:9999,
                      background:'#9b89b318',color:'#5e4a7f',
                    }}>
                      {label}
                    </span>
                  ))}
                </div>
              </Section>
            )}

            {/* 케이스 메타 — 발행 소요 시간 / 토큰 사용량 / 보호자 확인 */}
            <Section title="📊 케이스 메타">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,fontSize:12}}>
                <Meta label="접수" value={formatDateTime(meta.submitted_at ?? meta.created_at)} />
                <Meta label="발행" value={formatDateTime(meta.sent_at)} />
                <Meta label="총 소요" value={formatElapsed(meta.submitted_at ?? meta.created_at, meta.sent_at)} />
                <Meta label="플랜" value={meta.plan_tier ?? "-"} />
                {research && (
                  <>
                    <Meta label="AI 토큰 (입력)" value={research.tokens_input?.toLocaleString() ?? "-"} />
                    <Meta label="AI 토큰 (출력)" value={research.tokens_output?.toLocaleString() ?? "-"} />
                  </>
                )}
                <Meta
                  label="보호자 확인"
                  value={report?.viewed_at ? formatDateTime(report.viewed_at) : "미확인"}
                  tone={report?.viewed_at ? "ok" : "warn"}
                />
                <Meta label="확인까지" value={formatElapsed(meta.sent_at, report?.viewed_at)} />
              </div>
            </Section>

            <Section title="📤 재발송 도구">
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <button
                    type="button"
                    onClick={resend}
                    disabled={resendStatus === "sending"}
                    style={{
                      flex:'1 1 160px',padding:'10px 14px',fontSize:12,fontWeight:700,
                      background: resendStatus === "ok" ? "#27ae6022" : "#2c5f8d",
                      color: resendStatus === "ok" ? "#1f7a3f" : "#fff",
                      border:'none',borderRadius:9999,
                      cursor: resendStatus === "sending" ? 'wait' : 'pointer',
                      fontFamily:'inherit',
                      opacity: resendStatus === "sending" ? 0.6 : 1,
                    }}>
                    {resendStatus === "sending"
                      ? "보내는 중..."
                      : resendStatus === "ok"
                        ? "✅ 알림 보냈어요"
                        : "🔔 보호자에게 재알림 보내기"}
                  </button>
                  <button
                    type="button"
                    onClick={copyHint}
                    style={{
                      flex:'1 1 160px',padding:'10px 14px',fontSize:12,fontWeight:700,
                      background:'#fff',color: copyStatus === "ok" ? '#1f7a3f' : '#2c5f8d',
                      border:'1px solid '+(copyStatus === "ok" ? '#1f7a3f' : '#2c5f8d'),
                      borderRadius:9999,cursor:'pointer',fontFamily:'inherit',
                    }}>
                    {copyStatus === "ok"
                      ? "✅ 복사됨"
                      : copyStatus === "err"
                        ? "복사 실패"
                        : "📋 카톡 안내 문구 복사"}
                  </button>
                  <button
                    type="button"
                    onClick={sendEmail}
                    disabled={emailStatus === "sending"}
                    style={{
                      flex:'1 1 160px',padding:'10px 14px',fontSize:12,fontWeight:700,
                      background: emailStatus === "ok" ? "#27ae6022" : "#fff",
                      color: emailStatus === "ok" ? "#1f7a3f" : "#e6852b",
                      border:'1px solid '+(emailStatus === "ok" ? '#1f7a3f' : '#e6852b'),
                      borderRadius:9999,
                      cursor: emailStatus === "sending" ? 'wait' : 'pointer',
                      fontFamily:'inherit',
                      opacity: emailStatus === "sending" ? 0.6 : 1,
                    }}>
                    {emailStatus === "sending"
                      ? "보내는 중..."
                      : emailStatus === "ok"
                        ? "✅ 이메일 보냈어요"
                        : "✉️ 보호자에게 이메일 발송"}
                  </button>
                </div>
                {resendError && (
                  <div style={{fontSize:11,color:'#c0392b',lineHeight:1.5}}>
                    재알림 실패: {resendError}
                  </div>
                )}
                {emailError && (
                  <div style={{fontSize:11,color:'#c0392b',lineHeight:1.5}}>
                    이메일 실패: {emailError}
                  </div>
                )}
                {atalkError && (
                  <div style={{fontSize:11,color:'#c0392b',lineHeight:1.5}}>
                    알림톡 실패: {atalkError}
                  </div>
                )}
                <div style={{marginTop:6,display:'flex',flexWrap:'wrap',gap:8}}>
                  <button
                    type="button"
                    onClick={sendAlimtalk}
                    disabled={atalkStatus === "sending"}
                    style={{
                      flex:'1 1 160px',padding:'10px 14px',fontSize:12,fontWeight:700,
                      background: atalkStatus === "ok" ? "#27ae6022" : "#fff",
                      color: atalkStatus === "ok" ? "#1f7a3f" : "#3B1E1E",
                      border:'1px solid '+(atalkStatus === "ok" ? '#1f7a3f' : '#F9E000'),
                      borderRadius:9999,
                      cursor: atalkStatus === "sending" ? 'wait' : 'pointer',
                      fontFamily:'inherit',
                      opacity: atalkStatus === "sending" ? 0.6 : 1,
                    }}>
                    {atalkStatus === "sending"
                      ? "보내는 중..."
                      : atalkStatus === "ok"
                        ? "✅ 알림톡 보냈어요"
                        : "💬 카카오 알림톡 발송"}
                  </button>
                </div>
                <div style={{fontSize:10,color:'#718096',lineHeight:1.6,marginTop:6}}>
                  · <b>재알림</b>은 보호자 앱 알림 목록에 새 항목으로 들어가요. 보호자가 알림을 지웠을 때 유용해요.<br/>
                  · <b>이메일</b>은 보호자 가입 이메일로 리포트 도착 안내를 보내요.<br/>
                  · <b>알림톡</b>은 보호자 전화번호로 카카오톡 알림을 보내요. (Solapi 연동 필요)<br/>
                  · <b>카톡 안내 문구</b>는 클립보드에 복사돼요. 짱샘 카카오톡에서 보호자에게 그대로 붙여넣어 보내주세요.<br/>
                  · <b>PDF 파일</b>이 필요하면 우측 미리보기 하단의 "📄 PDF로 저장 · 공유" 버튼을 누른 뒤 카톡에 첨부하세요.
                </div>
              </div>
            </Section>

            <SimilarCases caseId={caseId} title="🔗 같은 신호의 다른 발행 케이스"/>
          </div>

          {/* 우측: phone frame에 ReportView */}
          <div className="archive-retro-phone">
            <div className="archive-retro-phone-frame">
              <div className="archive-retro-phone-notch">
                <div className="archive-retro-phone-notch-bar"/>
              </div>
              <div className="archive-retro-phone-scroll">
                <div style={{padding:"14px 8px 4px"}}>
                  <div style={{fontSize:13,fontWeight:800,padding:"0 12px"}}>
                    💌 {caseRecord?.concern_tags?.[0]
                      ? `${caseRecord.concern_tags[0]} 관찰 리포트`
                      : `${caseRecord?.it_children?.name ?? "아이"} 리포트`}
                  </div>
                </div>
                <ReportView caseRecord={caseRecord} report={report} loading={false}/>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{background:'#fff',borderRadius:14,padding:'14px 16px',boxShadow:'var(--shadow-sm)',marginBottom:12}}>
      <div style={{fontSize:11,fontWeight:800,color:'#4a5568',textTransform:'uppercase',letterSpacing:.4,marginBottom:10}}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Meta({ label, value, tone }) {
  const color = tone === "warn" ? "#c0392b" : tone === "ok" ? "#1f7a3f" : "#1a1a2e";
  return (
    <div style={{padding:'8px 10px',background:'#fafafa',borderRadius:8}}>
      <div style={{fontSize:10,fontWeight:700,color:'#718096',marginBottom:2}}>{label}</div>
      <div style={{fontSize:12,fontWeight:700,color,fontVariantNumeric:'tabular-nums'}}>{value}</div>
    </div>
  );
}
